import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ExternalLink, CheckCircle2, Sparkles, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { BANK_INFO } from "@/data/services";

// Predicciones cumplidas — videos reales de TikTok
const PREDICTIONS = [
  {
    id: "7624607257208048916",
    url: "https://www.tiktok.com/@crisangelastral/video/7624607257208048916",
    titleEs: "Terremoto en Indonesia",
    titleEn: "Earthquake in Indonesia",
    descEs: "Predicción cumplida sobre el terremoto que sacudió Indonesia.",
    descEn: "Fulfilled prediction about the earthquake that shook Indonesia.",
  },
  {
    id: "7560236703185784071",
    url: "https://www.tiktok.com/@crisangelastral/video/7560236703185784071",
    titleEs: "Inundaciones en Veracruz",
    titleEn: "Floods in Veracruz",
    descEs: "Predicción cumplida sobre las inundaciones que afectaron Veracruz.",
    descEn: "Fulfilled prediction about the floods that affected Veracruz.",
  },
  {
    id: "7612067654785879316",
    url: "https://www.tiktok.com/@crisangelastral/video/7612067654785879316",
    titleEs: "El Líder Supremo de Irán",
    titleEn: "The Supreme Leader of Iran",
    descEs: "Predicción cumplida sobre el Líder Supremo de Irán.",
    descEn: "Fulfilled prediction about the Supreme Leader of Iran.",
  },
];

const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.55a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84z" />
  </svg>
);

export const PredictionsSection = ({ variant = "page" }) => {
  const { lang } = useLanguage();
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (scriptLoaded.current) return;
    const existing = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://www.tiktok.com/embed.js";
      script.async = true;
      document.body.appendChild(script);
    }
    scriptLoaded.current = true;
  }, []);

  const copy = {
    eyebrow: lang === "es" ? "Predicciones Cumplidas" : "Fulfilled Predictions",
    title: lang === "es" ? "Lo que predije,\nsucedió." : "What I predicted,\nhappened.",
    subtitle:
      lang === "es"
        ? "Documentado y verificable en TikTok. Llevo 27 predicciones cumplidas; en esta página mostramos algunas, pero puedes ver todas en mi perfil en cualquier momento."
        : "Documented and verifiable on TikTok. I have 27 fulfilled predictions; this page shows a few of them, but you can see them all on my profile anytime.",
    badge: lang === "es" ? "Predicción Cumplida" : "Fulfilled Prediction",
    cta: lang === "es" ? "Ver más predicciones en TikTok" : "See more predictions on TikTok",
    handle: BANK_INFO.tiktokHandle,
  };

  return (
    <section
      id="predicciones"
      className={`relative ${variant === "page" ? "min-h-screen pt-28 pb-20" : "py-24 lg:py-32"} px-6 lg:px-12 overflow-hidden`}
      data-testid="predictions-section"
    >
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-[#d4af37]/8 via-transparent to-[#5A0001]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <Sparkles className="w-4 h-4" />
            <span className="uppercase tracking-[0.3em] text-xs">{copy.eyebrow}</span>
            <Sparkles className="w-4 h-4" />
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-white mb-4 leading-tight whitespace-pre-line">
            {copy.title.split("\n").map((line, i) => (
              <span key={i} className="block">
                {i === 1 ? <span className="gold-text italic">{line}</span> : line}
              </span>
            ))}
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto leading-relaxed mb-6">
            {copy.subtitle}
          </p>

          <a
            data-testid="predictions-handle"
            href={BANK_INFO.tiktok}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/60 border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all group"
          >
            <TikTokIcon className="w-4 h-4 text-[#d4af37]" />
            <span className="font-semibold gold-text">{copy.handle}</span>
            <ExternalLink className="w-3.5 h-3.5 text-neutral-500 group-hover:text-[#d4af37] transition-colors" />
          </a>
        </motion.div>

        {/* Video grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 justify-items-center">
          {PREDICTIONS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="w-full max-w-[360px] flex flex-col"
              data-testid={`prediction-card-${p.id}`}
            >
              {/* Header card */}
              <div className="glass-dark rounded-t-2xl p-5 border-x border-t border-[#d4af37]/30">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-widest mb-3">
                  <CheckCircle2 className="w-3 h-3" />
                  {copy.badge}
                </div>
                <h3 className="font-serif text-xl text-white mb-1.5 leading-tight">
                  {lang === "es" ? p.titleEs : p.titleEn}
                </h3>
                <p className="text-xs text-neutral-400 leading-relaxed">
                  {lang === "es" ? p.descEs : p.descEn}
                </p>
              </div>

              {/* TikTok embed */}
              <div className="flex justify-center bg-black/40 border-x border-[#d4af37]/30 pt-1">
                <blockquote
                  className="tiktok-embed"
                  cite={p.url}
                  data-video-id={p.id}
                  style={{ maxWidth: "325px", minWidth: "325px", margin: 0 }}
                >
                  <section>
                    <a target="_blank" rel="noreferrer" href={p.url}>
                      @{BANK_INFO.tiktokHandle.replace("@", "")}
                    </a>
                  </section>
                </blockquote>
              </div>

              {/* Footer */}
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`prediction-link-${p.id}`}
                className="glass-dark rounded-b-2xl px-5 py-3 border-x border-b border-[#d4af37]/30 flex items-center justify-between hover:bg-[#d4af37]/5 transition-colors group"
              >
                <span className="flex items-center gap-2 text-xs text-neutral-400">
                  <Calendar className="w-3.5 h-3.5 text-[#d4af37]" />
                  {lang === "es" ? "Verificable en TikTok" : "Verifiable on TikTok"}
                </span>
                <ExternalLink className="w-4 h-4 text-[#d4af37] group-hover:translate-x-0.5 transition-transform" />
              </a>
            </motion.div>
          ))}
        </div>

        {/* CTA bottom */}
        <div className="text-center mt-14">
          <a
            data-testid="predictions-cta-profile"
            href={BANK_INFO.tiktok}
            target="_blank"
            rel="noreferrer"
            className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full uppercase text-xs tracking-[0.25em]"
          >
            <TikTokIcon className="w-4 h-4" />
            {copy.cta}
          </a>
        </div>
      </div>
    </section>
  );
};
